import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function BookingInquiryDialog({ pkg, trigger }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    travel_date: "",
    travelers: 2,
    message: "",
  });

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", {
        ...form,
        travelers: Number(form.travelers) || 1,
        package_id: pkg?.id,
        package_title: pkg?.title,
      });
      toast.success("Inquiry sent! Our team will reach out within 24 hours.");
      setOpen(false);
      setForm({
        customer_name: "",
        email: "",
        phone: "",
        travel_date: "",
        travelers: 2,
        message: "",
      });
    } catch (err) {
      toast.error("Could not send inquiry. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="max-w-lg"
        data-testid="booking-inquiry-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {pkg ? `Enquire — ${pkg.title}` : "Plan my Andaman trip"}
          </DialogTitle>
          <p className="text-sm text-[color:var(--brand-text-soft)]">
            Share a few details and our travel curator will call you back with
            a custom quote.
          </p>
        </DialogHeader>
        <form
          onSubmit={submit}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="booking-inquiry-form"
        >
          <div className="md:col-span-2">
            <Label htmlFor="customer_name">Full name</Label>
            <Input
              id="customer_name"
              data-testid="input-name"
              value={form.customer_name}
              onChange={onChange("customer_name")}
              placeholder="Your name"
              required
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="input-email"
              value={form.email}
              onChange={onChange("email")}
              placeholder="you@example.com"
              required
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone (WhatsApp)</Label>
            <Input
              id="phone"
              data-testid="input-phone"
              value={form.phone}
              onChange={onChange("phone")}
              placeholder="+91 89009 64069"
              required
            />
          </div>
          <div>
            <Label htmlFor="travel_date">Preferred travel date</Label>
            <Input
              id="travel_date"
              type="date"
              data-testid="input-travel-date"
              value={form.travel_date}
              onChange={onChange("travel_date")}
            />
          </div>
          <div>
            <Label htmlFor="travelers">Travelers</Label>
            <Input
              id="travelers"
              type="number"
              min={1}
              data-testid="input-travelers"
              value={form.travelers}
              onChange={onChange("travelers")}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="message">Anything special?</Label>
            <Textarea
              id="message"
              data-testid="input-message"
              value={form.message}
              onChange={onChange("message")}
              placeholder="Honeymoon, kids' ages, dietary preferences, activities you love…"
              rows={3}
            />
          </div>
          <div className="md:col-span-2 flex justify-end pt-2">
            <Button
              type="submit"
              disabled={loading}
              data-testid="submit-inquiry"
              className="btn-primary"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Send inquiry
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
